import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    MicrophoneIcon,
    VideoCameraIcon,
    VideoCameraSlashIcon,
    ComputerDesktopIcon,
    StopIcon,
    UserGroupIcon,
    SpeakerXMarkIcon,
    HandRaisedIcon,
    EllipsisHorizontalIcon,
    PauseIcon,
    PlayIcon,
    PhotoIcon,
} from '@heroicons/react/24/solid';
import { getLiveClassState, useLiveClassState } from '../../../hooks/liveClassStore';
import {
    canControlRecording,
    canFreezeScreen,
    canMuteOthers,
    canRaiseHand,
    canShareScreen,
} from '../../../utils/liveClassPermissions';
import './live-floating-controls.css';

/** Tiempo sin mover el mouse antes de esconder la barra. */
const IDLE_HIDE_MS = 2600;
const IDLE_HIDE_WHILE_SHARING_MS = 1600;
/** Alto de la franja inferior donde la barra se queda fija mientras haya mouse. */
const HOVER_BAND_PX = 96;

const RecordDot = ({ paused }) => (
    <span
        className={`vc-float__record-dot${paused ? ' is-paused' : ''}`}
        aria-hidden="true"
    />
);

const FloatingButton = ({
    label,
    title,
    onClick,
    active = false,
    danger = false,
    hand = false,
    disabled = false,
    badge = null,
    children,
}) => (
    <button
        type="button"
        className={[
            'vc-float__btn',
            active ? 'is-active' : '',
            danger ? 'is-danger' : '',
            hand ? 'is-hand' : '',
        ].filter(Boolean).join(' ')}
        title={title || label}
        aria-label={title || label}
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
    >
        <span className="vc-float__icon">{children}</span>
        <span className="vc-float__label">{label}</span>
        {badge ? <span className="vc-float__badge">{badge}</span> : null}
    </button>
);

/**
 * Barra flotante sobre el video.
 *
 * Lee el mismo estado central que la barra superior y el tab «En vivo»: aquí no
 * se guarda nada propio más que si el menú «Más» está abierto.
 */
export const LiveFloatingControls = ({
    onToggleAudio,
    onToggleVideo,
    onToggleShare,
    onToggleFreeze,
    freezeBusy = false,
    onStartRecording,
    onStopRecording,
    onPauseRecording,
    onResumeRecording,
    onOpenLiveTab,
    onMuteAll,
    onToggleHand,
}) => {
    const microphoneEnabled = useLiveClassState((s) => s.microphoneEnabled);
    const cameraEnabled = useLiveClassState((s) => s.cameraEnabled);
    const screenShareEnabled = useLiveClassState((s) => s.screenShareEnabled);
    const activeShareUserId = useLiveClassState((s) => s.activeShareUserId);
    const screenFrozen = useLiveClassState((s) => s.screenFrozen);
    const recordingState = useLiveClassState((s) => s.recordingState);
    const recordingBusy = useLiveClassState((s) => s.recordingBusy);
    const isHost = useLiveClassState((s) => s.isHost);
    const shareLocked = useLiveClassState((s) => s.shareLocked);
    const connection = useLiveClassState((s) => s.connection);
    const participants = useLiveClassState((s) => s.participants);
    const raisedHandUserIds = useLiveClassState((s) => s.raisedHandUserIds);
    const selfUserId = useLiveClassState((s) => s.selfUserId);

    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef(null);
    const rootRef = useRef(null);

    const [revealed, setRevealed] = useState(true);
    const hideTimerRef = useRef(null);
    // Mientras el menú está abierto o el mouse está sobre la barra, no se esconde.
    const pinnedRef = useRef(false);

    const permissionState = useMemo(
        () => ({ isHost, shareLocked, connection }),
        [isHost, shareLocked, connection],
    );

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const scheduleHide = useCallback(() => {
        clearHideTimer();
        const sharing = getLiveClassState().screenShareEnabled
            || getLiveClassState().activeShareUserId != null;
        const delay = sharing ? IDLE_HIDE_WHILE_SHARING_MS : IDLE_HIDE_MS;
        hideTimerRef.current = window.setTimeout(() => setRevealed(false), delay);
    }, [clearHideTimer]);

    const reveal = useCallback(({ keep = false } = {}) => {
        setRevealed(true);
        if (keep || pinnedRef.current) clearHideTimer();
        else scheduleHide();
    }, [clearHideTimer, scheduleHide]);

    /**
     * El mouse sobre el área de video revela la barra; si se queda en la franja
     * inferior se mantiene, y al salir del video desaparece de inmediato.
     */
    useEffect(() => {
        const stage = rootRef.current?.closest('.vc-stage');
        if (!stage) return undefined;

        const onMove = (event) => {
            if (pinnedRef.current) return;
            const rect = stage.getBoundingClientRect();
            const band = Math.max(HOVER_BAND_PX, rect.height * 0.2);
            reveal({ keep: event.clientY > rect.bottom - band });
        };

        const onLeave = () => {
            if (pinnedRef.current) return;
            clearHideTimer();
            setRevealed(false);
        };

        stage.addEventListener('pointermove', onMove);
        stage.addEventListener('pointerdown', onMove);
        stage.addEventListener('pointerleave', onLeave);
        scheduleHide();

        return () => {
            stage.removeEventListener('pointermove', onMove);
            stage.removeEventListener('pointerdown', onMove);
            stage.removeEventListener('pointerleave', onLeave);
            clearHideTimer();
        };
    }, [reveal, scheduleHide, clearHideTimer]);

    useEffect(() => {
        pinnedRef.current = moreOpen;
        if (moreOpen) reveal({ keep: true });
        else scheduleHide();
    }, [moreOpen, reveal, scheduleHide]);

    // Un cambio de estado importante se confirma mostrando la barra un momento.
    useEffect(() => {
        reveal();
    }, [recordingState, screenFrozen, screenShareEnabled, activeShareUserId, reveal]);

    const pinBar = () => {
        pinnedRef.current = true;
        clearHideTimer();
        setRevealed(true);
    };

    const releaseBar = () => {
        if (moreOpen) return;
        pinnedRef.current = false;
        scheduleHide();
    };

    useEffect(() => {
        if (!moreOpen) return undefined;
        const onDoc = (event) => {
            if (moreRef.current && !moreRef.current.contains(event.target)) {
                setMoreOpen(false);
            }
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setMoreOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [moreOpen]);

    const handRaised = selfUserId != null && raisedHandUserIds.includes(selfUserId);
    const showFreeze = canFreezeScreen(permissionState);
    const showRecording = canControlRecording(permissionState);
    const showMuteAll = canMuteOthers(permissionState);
    const showShare = canShareScreen(permissionState);
    const showHand = canRaiseHand(permissionState);
    const busy = connection !== 'connected';

    const runAndClose = (fn) => () => {
        setMoreOpen(false);
        fn?.();
    };

    /** Acciones secundarias: en escritorio van en la barra, en móvil en «Más». */
    const secondaryActions = [];

    if (showFreeze) {
        let freezeTitle = 'Los alumnos se quedan con la imagen actual mientras usas tu computadora';
        if (screenFrozen) freezeTitle = 'Volver a transmitir tu pantalla en vivo';
        else if (!screenShareEnabled) freezeTitle = 'Comparte tu pantalla para poder congelarla';
        secondaryActions.push({
            id: 'freeze',
            label: screenFrozen ? 'Descongelar pantalla' : 'Congelar pantalla',
            title: freezeTitle,
            active: screenFrozen,
            disabled: freezeBusy || (!screenFrozen && !screenShareEnabled),
            icon: screenFrozen ? <PlayIcon /> : <PhotoIcon />,
            onClick: onToggleFreeze,
        });
    }

    if (showRecording) {
        if (recordingState === 'idle') {
            secondaryActions.push({
                id: 'rec-start',
                label: 'Iniciar grabación',
                title: 'Grabar la clase en Zoom',
                disabled: recordingBusy,
                icon: <RecordDot />,
                onClick: onStartRecording,
            });
        } else if (recordingState === 'recording') {
            secondaryActions.push({
                id: 'rec-pause',
                label: 'Pausar grabación',
                title: 'Pausa solo la grabación; la clase sigue normal',
                active: true,
                disabled: recordingBusy,
                icon: <PauseIcon />,
                onClick: onPauseRecording,
            });
            secondaryActions.push({
                id: 'rec-stop',
                label: 'Detener grabación',
                title: 'Termina la grabación y la envía a procesar',
                danger: true,
                disabled: recordingBusy,
                icon: <StopIcon />,
                onClick: onStopRecording,
            });
        } else {
            secondaryActions.push({
                id: 'rec-resume',
                label: 'Reanudar grabación',
                title: 'Continuar grabando la misma clase',
                active: true,
                disabled: recordingBusy,
                icon: <PlayIcon />,
                onClick: onResumeRecording,
            });
            secondaryActions.push({
                id: 'rec-stop',
                label: 'Detener grabación',
                title: 'Termina la grabación y la envía a procesar',
                danger: true,
                disabled: recordingBusy,
                icon: <StopIcon />,
                onClick: onStopRecording,
            });
        }
    }

    secondaryActions.push({
        id: 'live-users',
        label: 'Usuarios en vivo',
        title: 'Ver quién está conectado a la clase',
        icon: <UserGroupIcon />,
        badge: participants.length || null,
        onClick: onOpenLiveTab,
    });

    if (showMuteAll) {
        secondaryActions.push({
            id: 'mute-all',
            label: 'Silenciar a todos',
            title: 'Silencia a los estudiantes sin afectar tu micrófono',
            icon: <SpeakerXMarkIcon />,
            onClick: onMuteAll,
        });
    }

    const anyoneSharing = screenShareEnabled || activeShareUserId != null;

    return (
        <div
            ref={rootRef}
            className={[
                'vc-float',
                revealed ? 'is-revealed' : 'is-hidden',
                anyoneSharing ? 'is-sharing' : '',
            ].filter(Boolean).join(' ')}
        >
            <div
                className="vc-float__chrome"
                role="toolbar"
                aria-label="Controles de la clase en vivo"
                aria-hidden={!revealed}
                onPointerEnter={pinBar}
                onPointerLeave={releaseBar}
                onFocusCapture={pinBar}
                onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) releaseBar();
                }}
            >
            {recordingState !== 'idle' ? (
                <span
                    className={`vc-float__rec-pill${recordingState === 'paused' ? ' is-paused' : ''}`}
                >
                    <RecordDot paused={recordingState === 'paused'} />
                    {recordingState === 'paused' ? 'Grabación pausada' : 'Grabando'}
                </span>
            ) : null}

            <div className="vc-float__bar">
                <FloatingButton
                    label={microphoneEnabled ? 'Micrófono' : 'Silenciado'}
                    title={microphoneEnabled ? 'Silenciar mi micrófono' : 'Activar mi micrófono'}
                    active={!microphoneEnabled}
                    danger={!microphoneEnabled}
                    disabled={busy}
                    onClick={onToggleAudio}
                >
                    <MicrophoneIcon />
                </FloatingButton>

                <FloatingButton
                    label={cameraEnabled ? 'Cámara' : 'Cámara off'}
                    title={cameraEnabled ? 'Apagar mi cámara' : 'Encender mi cámara'}
                    active={!cameraEnabled}
                    danger={!cameraEnabled}
                    disabled={busy}
                    onClick={onToggleVideo}
                >
                    {cameraEnabled ? <VideoCameraIcon /> : <VideoCameraSlashIcon />}
                </FloatingButton>

                {showShare ? (
                    <FloatingButton
                        label={screenShareEnabled ? 'Dejar de compartir' : 'Compartir'}
                        title={screenShareEnabled
                            ? 'Dejar de compartir pantalla'
                            : 'Compartir pantalla'}
                        active={screenShareEnabled}
                        danger={screenShareEnabled}
                        disabled={busy}
                        onClick={onToggleShare}
                    >
                        {screenShareEnabled ? <StopIcon /> : <ComputerDesktopIcon />}
                    </FloatingButton>
                ) : null}

                {showHand ? (
                    <FloatingButton
                        label={handRaised ? 'Bajar mano' : 'Levantar mano'}
                        title={handRaised
                            ? 'Bajar la mano'
                            : 'Avisar al profesor que quieres participar'}
                        active={handRaised}
                        hand
                        disabled={busy}
                        onClick={onToggleHand}
                    >
                        <HandRaisedIcon />
                    </FloatingButton>
                ) : null}

                {/* Escritorio: acciones secundarias visibles en la barra. */}
                <span className="vc-float__sep vc-float__wide-only" aria-hidden="true" />

                {secondaryActions.map((action) => (
                    <span key={action.id} className="vc-float__wide-only">
                        <FloatingButton
                            label={action.label}
                            title={action.title}
                            active={action.active}
                            danger={action.danger}
                            disabled={busy || action.disabled}
                            badge={action.badge}
                            onClick={action.onClick}
                        >
                            {action.icon}
                        </FloatingButton>
                    </span>
                ))}

                {/* Pantallas chicas: todo lo secundario detrás de «Más». */}
                <div className="vc-float__more vc-float__narrow-only" ref={moreRef}>
                    <FloatingButton
                        label="Más"
                        title="Más controles de la clase"
                        active={moreOpen}
                        onClick={() => setMoreOpen((open) => !open)}
                    >
                        <EllipsisHorizontalIcon />
                    </FloatingButton>

                    {moreOpen ? (
                        <div className="vc-float__menu" role="menu">
                            {secondaryActions.map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    role="menuitem"
                                    className={action.danger ? 'is-danger' : ''}
                                    disabled={busy || action.disabled}
                                    title={action.title}
                                    onClick={runAndClose(action.onClick)}
                                >
                                    <span className="vc-float__menu-icon">{action.icon}</span>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            </div>
        </div>
    );
};
